import { Injectable } from '@angular/core';

import { AuthService } from './auth.service';

const HANDOVER_HASH_PATTERN = /^#ht=(.+)$/;

/**
 * Bootstrap-time consumer for a UI-switcher handover (PD-18256). Reads a
 * `#ht=<uuid>` fragment left by Phoenix, exchanges it for a session, and
 * hydrates AuthService the same way hydrateOidcSession does for OIDC,
 * without the normal /user/login call. Strips the fragment before the
 * exchange call so a refresh can't replay an already-spent token.
 */
@Injectable({ providedIn: 'root' })
export class HandoverGuard {
  constructor(private _auth: AuthService) {}

  async consume(): Promise<void> {
    const match = HANDOVER_HASH_PATTERN.exec(window.location.hash);
    if (!match) {
      return;
    }

    const [, handoverToken] = match;
    window.history.replaceState(
      null,
      '',
      window.location.pathname + window.location.search
    );

    try {
      const { session } = await this._auth
        .exchangeHandoverToken(handoverToken)
        .toPromise();
      await this._auth.hydrateFromHandover(session).toPromise();
    } catch {
      // Expired, replayed, or invalid, fail open to the normal login page.
    }
  }
}

export function handoverInitializerFactory(
  handoverGuard: HandoverGuard
): () => Promise<void> {
  return () => handoverGuard.consume();
}
